import React from "react";
import {
  FaCreditCard,
  FaMoneyBillWave,
  FaWallet,
  FaBuilding,
} from "react-icons/fa";

const PaymentMethodSelector = ({
  paymentMode,
  selectedMethod,
  onMethodChange,
  walletBalance = 0,
  fare = 0,
  isLoading = false,
  availableOptions = {},
}) => {
  // Get available methods based on payment mode
  const getAvailableMethods = () => {
    if (paymentMode === "COMPANY") {
      return [
        {
          key: "COMPANY",
          label: "Company Account",
          icon: FaBuilding,
          color: "indigo",
          description: "Bill to company account",
        },
      ];
    }

    const queryParams = new URLSearchParams(window.location.search);
    const paymentOption = queryParams.get("paymentOption") || "full";
    const isPartial = paymentOption === "partial";

    return [
      {
        key: "COD",
        label: "Cash on Delivery",
        icon: FaMoneyBillWave,
        color: "green",
        description: "Pay when you reach your destination",
        available: !isPartial && (availableOptions?.personal?.methods?.cod?.available !== false || availableOptions?.personal?.methods === undefined),
        reason: isPartial ? "COD is not available for partial advance payments" : undefined,
      },
      {
        key: "ONLINE",
        label: "Online Payment",
        icon: FaCreditCard,
        color: "blue",
        description: "Pay with card, UPI, or digital wallet",
        available: availableOptions?.personal?.methods?.online?.available !== false || availableOptions?.personal?.methods === undefined,
      },
      {
        key: "WALLET",
        label: "My Wallet",
        icon: FaWallet,
        color: "purple",
        description: `Pay using your wallet balance`,
        available: availableOptions?.personal?.methods?.wallet?.available === true,
        balance: availableOptions?.personal?.methods?.wallet?.balance || walletBalance,
        reason: availableOptions?.personal?.methods?.wallet?.reason,
      },
    ];
  };

  const availableMethods = getAvailableMethods();
  const colorMap = {
    indigo: { border: "#6366F1", bg: "#E0E7FF", icon: "#4F46E5", textColor: "#4338CA" },
    green: { border: "#22C55E", bg: "#DCFCE7", icon: "#16A34A", textColor: "#15803D" },
    blue: { border: "#3B82F6", bg: "#DBEAFE", icon: "#2563EB", textColor: "#1D4ED8" },
    purple: { border: "#A855F7", bg: "#F3E8FF", icon: "#9333EA", textColor: "#7E22CE" },
  };

  return (
    <div className="space-y-4">
      <h3 className="font-semibold text-base text-gray-800 mb-4">
        {paymentMode === "COMPANY"
          ? "Payment via Company Account"
          : "Choose Your Payment Method"}
      </h3>

      <div className="space-y-3">
        {availableMethods.map((method) => {
          const Icon = method.icon;
          const colors = colorMap[method.color];
          const isSelected = selectedMethod === method.key;
          const isDisabled = method.available === false || isLoading;

          return (
            <label
              key={method.key}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "1rem",
                border: `2px solid ${isSelected ? colors.border : "#E5E7EB"}`,
                borderRadius: "0.75rem",
                padding: "1rem",
                cursor: isDisabled ? "not-allowed" : "pointer",
                backgroundColor: isSelected ? colors.bg : "white",
                opacity: isDisabled ? 0.5 : 1,
                transition: "all 0.2s ease",
              }}
              className={`${isSelected ? "" : "hover:shadow-md"}`}
              onClick={(e) => {
                // If clicking on label and not disabled, trigger the input
                if (!isDisabled && e.target.tagName !== "INPUT") {
                  onMethodChange(method.key);
                }
              }}
            >
              <input
                type="radio"
                name="paymentMethod"
                value={method.key}
                checked={isSelected}
                onChange={(e) => {
                  if (!isDisabled) {
                    onMethodChange(e.target.value);
                  }
                }}
                disabled={isDisabled}
                style={{
                  cursor: isDisabled ? "not-allowed" : "pointer",
                  pointerEvents: "auto",
                  marginTop: "0.25rem",
                }}
              />

              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.25rem" }}>
                  <Icon style={{ color: colors.icon, fontSize: "1.125rem" }} />
                  <p style={{ fontWeight: "600" }}>{method.label}</p>
                </div>
                <p style={{ fontSize: "0.875rem", color: "#4B5563" }}>
                  {method.description}
                </p>

                {method.key === "WALLET" && method.available && (
                  <div style={{ marginTop: "0.5rem", fontSize: "0.875rem" }}>
                    <p style={{ color: colors.textColor, fontWeight: "600" }}>
                      Balance: ₹{(method.balance || 0).toLocaleString("en-IN")}
                    </p>
                    {method.balance < fare && (
                      <p style={{ color: "#EA580C", fontSize: "0.75rem", marginTop: "0.25rem" }}>
                        ⚠️ Insufficient balance for this trip
                      </p>
                    )}
                  </div>
                )}

                {!method.available && method.reason && (
                  <p style={{ fontSize: "0.75rem", color: "#DC2626", marginTop: "0.5rem" }}>{method.reason}</p>
                )}
              </div>
            </label>
          );
        })}
      </div>

      {/* Info Box */}
      <div style={{
        backgroundColor: "#DBEAFE",
        borderLeft: "4px solid #3B82F6",
        padding: "1rem",
        borderRadius: "0.375rem",
        fontSize: "0.875rem",
        color: "#374151",
      }}>
        {paymentMode === "COMPANY" ? (
          <p>
            <strong>Note:</strong> This ride will be billed to your company
            account. The amount will be deducted from your company's wallet or
            credit limit.
          </p>
        ) : selectedMethod === "COD" ? (
          <p>
            <strong>Reminder:</strong> You'll pay the driver directly when the
            ride is completed.
          </p>
        ) : selectedMethod === "ONLINE" ? (
          <p>
            <strong>Secure Payment:</strong> You'll be redirected to a secure
            payment gateway to complete your payment.
          </p>
        ) : selectedMethod === "WALLET" ? (
          <p>
            <strong>Instant Deduction:</strong> Amount will be deducted from
            your wallet immediately.
          </p>
        ) : null}
      </div>
    </div>
  );
};

export default PaymentMethodSelector;
